import React, { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Stack, TextField, Typography, styled, AccordionProps } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditIcon from '@mui/icons-material/Edit';
import { FormProvider, useForm } from 'react-hook-form';
import { AttachmentsSection } from '../CreateNewServiceCall/ExplanationStep/AttachmentsSection';
import { useIntl } from 'react-intl';
import { commonMessages } from 'utils/commonMessages';
import { ServiceCallsApi } from 'modules/service-call/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LoadingButton } from '@mui/lab';

interface ServiceCallNoteReplyAccordionFormData {
  note: string
  attachments: { file: File }[]
}

interface ServiceCallNoteReplyAccordionProps {
  serviceCallId: string
}

const StyledAccordion = styled(Accordion)<AccordionProps>(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.info.main,
  borderRadius: 4,
  '& .MuiAccordionSummary-root': {
    backgroundColor: theme.palette.primary.main,
  },
  '& .MuiAccordionSummary-root:hover': {
    backgroundColor: theme.palette.primary.dark,
  }
}));

export const ServiceCallNoteReplyAccordion: React.FC<ServiceCallNoteReplyAccordionProps> = ({ serviceCallId }) => {
  const methods = useForm<ServiceCallNoteReplyAccordionFormData>({
    defaultValues: {
      attachments: []
    }
  });
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const [expanded, setExpanded] = useState(false);

  const addNoteMutation = useMutation((form: ServiceCallNoteReplyAccordionFormData) =>
    ServiceCallsApi.addNote(serviceCallId, form.note, form.attachments.map(attachment => attachment.file)), {
    onSuccess: () => {
      queryClient.invalidateQueries(['service-call', serviceCallId]);
      methods.reset({ note: '', attachments: [] });
      setExpanded(false);
    },
  });

  const onSubmit = (formData: ServiceCallNoteReplyAccordionFormData) => {
    addNoteMutation.mutate(formData);
  };

  return (
    <FormProvider {...methods} >
      <StyledAccordion disableGutters expanded={expanded} onChange={() => setExpanded(expanded => !expanded)}>
        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header" expandIcon={<ArrowDropDownIcon sx={{ color: 'white' }} />}>
          <Stack direction='row' gap={2} alignItems='center'>
            <EditIcon sx={{ color: 'white' }} />
            <Typography variant='h6' color='white'>{formatMessage({ id: 'ServiceCallNoteReplyAccordion.Title', defaultMessage: 'Reply' })}</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack gap={2} paddingTop={1}>
            <TextField
              {...methods.register('note', { required: formatMessage(commonMessages.fieldIsRequired) })}
              placeholder={formatMessage({ id: 'ServiceCallNoteReplyAccordion.YourMessage', defaultMessage: 'Your message' })}
              disabled={addNoteMutation.isLoading}
              error={Boolean(methods.formState.errors.note)}
              helperText={methods.formState.errors.note?.message}
              multiline
              minRows={5}
            />
            <AttachmentsSection disabled={addNoteMutation.isLoading} />
            <LoadingButton loading={addNoteMutation.isLoading} variant='contained' fullWidth onClick={methods.handleSubmit(onSubmit)}>{formatMessage(commonMessages.submit)}</LoadingButton>
          </Stack >
        </AccordionDetails>
      </StyledAccordion >
    </FormProvider >
  );
};